<cs-header>
  <cs-header-title>{{ LL.navigation.settings() }}</cs-header-title>
</cs-header>

<cs-content>
  <cs-card-list #cardList>

    <!-- Regional & Display Preferences -->
    <cs-card>
      <cs-card-title>{{ LL.settings.sections.regional() }}</cs-card-title>
      <cs-card-content>
        <div class="settings-section">
          <!-- Language -->
          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label">{{ LL.settings.language() }}</div>
              <div class="setting-description">{{ LL.settings.descriptions.language() }}</div>
            </div>
            <div class="setting-control">
              <!-- opt.label is a language's own native name (e.g. "Español"), which by
                   convention is never translated -->
              <cs-select [value]="settings.uiLanguage || 'en'" (valueChange)="onUiLanguageChangeAsync($event)">
                @for (opt of languageOptions; track opt.value) {
                <cs-select-option [value]="opt.value" [label]="opt.label | untranslated"></cs-select-option>
                }
              </cs-select>
            </div>
          </div>

          <!-- Clock Mode -->
          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label">{{ LL.settings.clockMode() }}</div>
              <div class="setting-description">{{ LL.settings.descriptions.clockMode({mode:
              settings.locale.clock24hr ? '24hr' : 'AM/PM'}) }}</div>
            </div>
            <div class="setting-control">
              <cs-select [(value)]="settings.clockHourMode">
                <cs-select-option value="{{ClockHourMode._24hr}}"
                [label]="LL.settings.hour24()"></cs-select-option>
                <cs-select-option value="{{ClockHourMode._12hr}}"
                [label]="LL.settings.ampm()"></cs-select-option>
                <cs-select-option value="{{ClockHourMode.Automatic}}"
                [label]="LL.settings.automatic()"></cs-select-option>
              </cs-select>
            </div>
          </div>

          <!-- Date Format -->
          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label">{{ LL.settings.dateFormat() }}</div>
              <div class="setting-description">{{ LL.settings.descriptions.dateFormat() }}</div>
            </div>
            <div class="setting-control">
              <!-- these are literal date-format examples, not translatable prose -->
              <cs-select [(value)]="settings.dateFormat">
                <cs-select-option value="{{DateFormat.US}}" [label]="'MM/DD/YY' | untranslated"></cs-select-option>
                <cs-select-option value="{{DateFormat.UK}}" [label]="'DD/MM/YY' | untranslated"></cs-select-option>
                <cs-select-option value="{{DateFormat.EUDot}}" [label]="'DD.MM.YY' | untranslated"></cs-select-option>
                <cs-select-option value="{{DateFormat.Automatic}}"
                [label]="LL.settings.automatic()"></cs-select-option>
              </cs-select>
            </div>
          </div>

          <!-- First Day of Week -->
          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label">{{ LL.settings.firstDayOfWeek() }}</div>
              <div class="setting-description">{{ LL.settings.descriptions.firstDayOfWeek() }}</div>
            </div>
            <div class="setting-control">
              <cs-select [(value)]="settings.firstDayOfWeek">
                <cs-select-option value="{{FirstDayOfWeek.Monday}}"
                [label]="LL.settings.monday()"></cs-select-option>
                <cs-select-option value="{{FirstDayOfWeek.Saturday}}"
                [label]="LL.settings.saturday()"></cs-select-option>
                <cs-select-option value="{{FirstDayOfWeek.Sunday}}"
                [label]="LL.settings.sunday()"></cs-select-option>
                <cs-select-option value="{{FirstDayOfWeek.Automatic}}"
                [label]="LL.settings.automatic()"></cs-select-option>
              </cs-select>
            </div>
          </div>

          <!-- Decimal Separator -->
          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label">{{ LL.settings.decimalSeparator() }}</div>
              <div class="setting-description">{{ LL.settings.descriptions.decimalSeparator() }}</div>
            </div>
            <div class="setting-control">
              <cs-select [(value)]="settings.locale.decimalSeparatorOverride">
                <cs-select-option value="." [label]="LL.settings.point()"></cs-select-option>
                <cs-select-option value="," [label]="LL.settings.comma()"></cs-select-option>
                <cs-select-option value="" [label]="LL.settings.automatic()"></cs-select-option>
              </cs-select>
            </div>
          </div>
        </div>
      </cs-card-content>
    </cs-card>

    <!-- Nutrition -->
    <cs-card>
      <cs-card-title>{{ LL.settings.sections.nutrition() }}</cs-card-title>
      <cs-card-content>
        <div class="settings-section">
          <!-- Nutrition Label Style -->
          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label">{{ LL.settings.nutritionLabelStyle() }}</div>
              <div class="setting-description">{{ LL.settings.descriptions.nutritionLabelStyle() }}</div>
            </div>
            <div class="setting-control">
              <cs-select [(value)]="settings.nutritionLabelStyle">
                <cs-select-option value="{{NutritionLabelStyle.US}}"
                [label]="LL.common.labelStyles.us()"></cs-select-option>
                <cs-select-option value="{{NutritionLabelStyle.EU}}"
                [label]="LL.common.labelStyles.eu()"></cs-select-option>
              </cs-select>
            </div>
          </div>
        </div>
      </cs-card-content>
    </cs-card>

    <!-- Accessibility -->
    <cs-card>
      <cs-card-title>{{ LL.settings.sections.accessibility() }}</cs-card-title>
      <cs-card-content>
        <div class="settings-section">
          <!-- Allow Card Animations -->
          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label">{{ LL.settings.allowCardAnimations() }}</div>
              <div class="setting-description">{{ LL.settings.descriptions.cardAnimations() }}</div>
            </div>
            <div class="setting-control">
              <cs-toggle [(checked)]="settings.cardAnimations" (checkedChange)="onCardAnimationsChange()"></cs-toggle>
            </div>
          </div>

          <!-- Header Hide Behavior -->
          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label">{{ LL.settings.headerHideBehavior() }}</div>
              <div class="setting-description">{{ LL.settings.descriptions.headerHideBehavior() }}</div>
            </div>
            <div class="setting-control">
              <cs-select [(value)]="settings.headerScrollHide">
                <cs-select-option value="{{ScrollHideMode.Always}}"
                [label]="LL.settings.scrollHide.always()"></cs-select-option>
                <cs-select-option value="{{ScrollHideMode.Never}}"
                [label]="LL.settings.scrollHide.never()"></cs-select-option>
                <cs-select-option value="{{ScrollHideMode.Auto}}"
                [label]="LL.settings.automatic()"></cs-select-option>
              </cs-select>
            </div>
          </div>

          <!-- Footer Hide Behavior -->
          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label">{{ LL.settings.footerHideBehavior() }}</div>
              <div class="setting-description">{{ LL.settings.descriptions.footerHideBehavior() }}</div>
            </div>
            <div class="setting-control">
              <cs-select [(value)]="settings.footerScrollHide">
                <cs-select-option value="{{ScrollHideMode.Always}}"
                [label]="LL.settings.scrollHide.always()"></cs-select-option>
                <cs-select-option value="{{ScrollHideMode.Never}}"
                [label]="LL.settings.scrollHide.never()"></cs-select-option>
                <cs-select-option value="{{ScrollHideMode.Auto}}"
                [label]="LL.settings.automatic()"></cs-select-option>
              </cs-select>
            </div>
          </div>
        </div>
      </cs-card-content>
    </cs-card>


    <!-- Test Settings (only visible in test mode) -->
    @if (settings.testMode) {
      <cs-card>
        <cs-card-title>{{ LL.settings.testSettings.header() }}</cs-card-title>
        <cs-card-content>
          <cs-item-list>
            <cs-item>
              <cs-label>{{ LL.settings.testSettings.testMode() }}</cs-label>
              <cs-toggle [(checked)]="settings.testMode"></cs-toggle>
            </cs-item>
            <cs-item>
              <cs-label>{{ LL.settings.testSettings.locale() }}</cs-label>
              <cs-input type="text" [(value)]="settings.locale.localeName"></cs-input>
            </cs-item>
            <cs-item>
              <cs-label>{{ LL.settings.testSettings.localeDecimalSeparator() }}</cs-label>
              <cs-input type="text" [(value)]="settings.locale.decimalSeparator"
              [placeholder]="LL.common.placeholders.enterText()"></cs-input>
            </cs-item>
            <cs-item>
              <cs-label>{{ LL.settings.testSettings.unitSettings() }}</cs-label>
              <cs-input type="text" [(value)]="settings.unitsPrefsJSON"
              [placeholder]="LL.common.placeholders.enterText()"></cs-input>
            </cs-item>
            <cs-item>
              <cs-label>{{ LL.settings.testSettings.hintsSeen() }}</cs-label>
              <cs-input type="text" [(value)]="settings.hintsSeenJSON"
              [placeholder]="LL.common.placeholders.enterText()"></cs-input>
            </cs-item>
            <cs-item>
              <cs-label>{{ LL.settings.testSettings.helpSeen() }}</cs-label>
              <cs-input type="text" [(value)]="settings.helpSeenJSON"
              [placeholder]="LL.common.placeholders.enterText()"></cs-input>
            </cs-item>
            <cs-item>
              <cs-label>{{ LL.settings.testSettings.dismissedToasts() }}</cs-label>
              <cs-input type="text" [(value)]="settings.permanentlyDismissedToastsJSON"
              [placeholder]="LL.common.placeholders.enterText()"></cs-input>
            </cs-item>
            <cs-note>
              {{ LL.settings.testSettings.resetHiddenMessagesNote() }}
            </cs-note>
            <cs-item>
              <cs-button small (click)="handleResetHiddenMessagesAsync()"
              [label]="LL.settings.testSettings.resetHiddenMessages()"></cs-button>
            </cs-item>
            <cs-item>
              <cs-label>{{ LL.settings.testSettings.showAllHints() }}</cs-label>
              <cs-toggle [(checked)]="settings.debugShowAllHints"></cs-toggle>
            </cs-item>
            <cs-item>
              <cs-label>{{ LL.settings.testSettings.showAllTutorials() }}</cs-label>
              <cs-toggle [(checked)]="settings.debugShowAllTutorials"></cs-toggle>
            </cs-item>
            <cs-item>
              <cs-label>{{ LL.settings.testSettings.debugFoodSearch() }}</cs-label>
              <cs-toggle [(checked)]="settings.debugFoodSearch"></cs-toggle>
            </cs-item>
            <cs-item>
              <cs-label>{{ LL.settings.testSettings.debugRestTimerOnWeb() }}</cs-label>
              <cs-toggle [(checked)]="settings.debugRestTimerOnWeb"></cs-toggle>
            </cs-item>
            <cs-item vertical>
              <cs-label>{{ LL.settings.testSettings.debugLog() }}</cs-label>
              <cs-textarea [(value)]="settings.debugLog" [rows]="8"
              [placeholder]="LL.settings.testSettings.debugLogPlaceholder()"></cs-textarea>
            </cs-item>
            <cs-item>
              <cs-button small (click)="clearDebugLog()"
              [label]="LL.settings.testSettings.clearLog()"></cs-button>
            </cs-item>
            <cs-item>
              <cs-label>{{ LL.settings.testSettings.accountDatabase() }}</cs-label>
              <cs-note>
                <p>{{ LL.settings.testSettings.cachedFoods({count: numCachedFoods}) }}</p>
                <p>{{ LL.settings.testSettings.uncacheFoodsNote() }}</p>
              </cs-note>
            </cs-item>
            <cs-item>
              <cs-button small (click)="handleUncacheFoods()"
              [label]="LL.common.buttons.uncacheFoods()"></cs-button>
            </cs-item>
            <cs-note>
              {{ LL.settings.testSettings.testSurveyNote() }}
            </cs-note>
            <cs-item>
              <cs-button small (click)="handleTestSurvey()" [label]="LL.common.buttons.testSurvey()"></cs-button>
            </cs-item>
            <cs-note>
              {{ LL.settings.testSettings.testNotificationNote() }}
            </cs-note>
            <cs-item>
              <cs-button small (click)="handleTestNotification()" [label]="LL.settings.testSettings.testNotification()"></cs-button>
            </cs-item>

            <!-- #4625 — store review prompt tools, their own section in here -->
            <cs-item-list-header>{{ 'Store review prompt' | untranslated }}</cs-item-list-header>
            <cs-note>
              {{ 'Fires the store\'s own prompt right now, ignoring the gate. It never appears in a TestFlight build and always appears in a development build. Neither store reports anything back, so nothing visible is not proof of a fault.' | untranslated }}
            </cs-note>
            <cs-item>
              <cs-button small (click)="handleShowReviewPrompt()"
                [label]="'Show Store Review Prompt' | untranslated"></cs-button>
            </cs-item>
            <cs-note>
              {{ 'Asks the gate what it would decide for this account, and names the reason when it refuses.' | untranslated }}
            </cs-note>
            <cs-item>
              <cs-button small (click)="handleExplainReviewPrompt()"
                [label]="'Why no review prompt?' | untranslated"></cs-button>
            </cs-item>
            <cs-note>
              {{ 'Says whether the last finished workout beat a personal record, as phase 2 would judge it. Ignores the gate and the phase.' | untranslated }}
            </cs-note>
            <cs-item>
              <cs-button small (click)="handleExplainPersonalRecord()"
                [label]="'Last Workout a Personal Record?' | untranslated"></cs-button>
            </cs-item>

          </cs-item-list>
        </cs-card-content>
      </cs-card>
    }

    <!-- Experimental (only visible in test mode) -->
    @if (settings.testMode) {
      <cs-card>
        <cs-card-title>{{ LL.settings.sections.experimental() }}</cs-card-title>
        <cs-card-content>
          <div class="settings-section">
            <!-- Show Slot Name -->
            <div class="setting-row">
              <div class="setting-info">
                <div class="setting-label">{{ LL.settings.experimental.showSlotName() }}</div>
                <div class="setting-description">{{ LL.settings.descriptions.showSlotName() }}</div>
              </div>
              <div class="setting-control">
                <cs-toggle [(checked)]="settings.showSlotName"></cs-toggle>
              </div>
            </div>
          </div>
        </cs-card-content>
      </cs-card>
    }

  </cs-card-list>
</cs-content>